// The fix lifecycle as a discriminated union (00-architecture §4).
//
// Defined now so illegal states do not compile; the transition MACHINE (the guarded
// transitions) is built in Slice 1. Each state carries only the fields that exist at
// that point in the lifecycle, so a `previewUrl` on a `queued` fix, or a `resolvedAt`
// before `released`, is a type error rather than a runtime surprise
// (app-engineering §2: make illegal states unrepresentable).
import { z } from "zod";
import { NonEmptyString } from "./common.js";
import { FixCategory, FixRisk } from "./enums.js";
/** Classification set at triage and carried forward through the happy path. */
const classified = {
    category: FixCategory,
    risk: FixRisk,
    touchesHardGate: z.boolean(),
};
const previewUrl = z.url();
const prUrl = z.url();
/**
 * The fix state. Discriminated on `status`. The happy path advances
 * queued -> triaged -> researching -> implementing -> testing -> staged -> gated ->
 * awaiting_tap -> releasing -> released -> retro_done; side states (escalated,
 * blocked, rolled_back, failed) and the build-flow pause (awaiting_manual_step) carry
 * their own payloads.
 */
export const FixState = z.discriminatedUnion("status", [
    z.strictObject({ status: z.literal("queued") }),
    z.strictObject({ status: z.literal("triaged"), ...classified }),
    z.strictObject({ status: z.literal("researching"), ...classified }),
    z.strictObject({ status: z.literal("implementing"), ...classified }),
    z.strictObject({ status: z.literal("testing"), ...classified }),
    z.strictObject({ status: z.literal("staged"), ...classified, previewUrl }),
    z.strictObject({ status: z.literal("gated"), ...classified, previewUrl }),
    z.strictObject({ status: z.literal("awaiting_tap"), ...classified, previewUrl }),
    z.strictObject({ status: z.literal("releasing"), ...classified, prUrl }),
    z.strictObject({ status: z.literal("released"), ...classified, prUrl, resolvedAt: z.iso.datetime() }),
    z.strictObject({ status: z.literal("retro_done"), ...classified, prUrl, resolvedAt: z.iso.datetime() }),
    z.strictObject({ status: z.literal("awaiting_manual_step"), ...classified }),
    // Side states. Payloads firm up in Slice 1.
    z.strictObject({ status: z.literal("escalated"), reason: NonEmptyString }),
    z.strictObject({ status: z.literal("blocked"), reason: NonEmptyString }),
    z.strictObject({ status: z.literal("rolled_back"), reason: NonEmptyString, prUrl }),
    z.strictObject({ status: z.literal("failed"), reason: NonEmptyString }),
]);
/** The closed set of fix status discriminants. */
export const FIX_STATUSES = [
    "queued",
    "triaged",
    "researching",
    "implementing",
    "testing",
    "staged",
    "gated",
    "awaiting_tap",
    "releasing",
    "released",
    "retro_done",
    "awaiting_manual_step",
    "escalated",
    "blocked",
    "rolled_back",
    "failed",
];
//# sourceMappingURL=fix-state.js.map